import type { ProfitAndLossData } from '../../types/finance';

interface Props {
    data: ProfitAndLossData;
}

const formatMoney = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
        minimumFractionDigits: 2
    }).format(amount);
};

// Line item row - FDS Typography: JetBrains Mono, tabular-nums
const LineItem = ({ label, value, indent = false }: { label: string; value: number; indent?: boolean }) => (
    <div className={`flex justify-between items-center py-2 ${indent ? 'pl-8' : ''}`}>
        <span className="text-base text-fg">{label}</span>
        <span className="font-mono text-base tabular-nums text-fg font-medium">{formatMoney(value)}</span>
    </div>
);

// Section total row with highlight
const TotalRow = ({ label, value, accent = false }: { label: string; value: number; accent?: boolean }) => (
    <div className={`flex justify-between items-center py-3 px-4 rounded-lg ${accent ? 'bg-success/10 border border-success/20' : 'bg-raised border border-line'}`}>
        <span className={`font-semibold font-display ${accent ? 'text-success uppercase tracking-wider text-sm' : 'text-fg text-base'}`}>{label}</span>
        <span className={`font-mono font-bold tabular-nums ${accent ? 'text-success text-xl' : 'text-fg text-xl'}`}>{formatMoney(value)}</span>
    </div>
);

export const ProfitAndLossCard = ({ data }: Props) => {
    // Revenue
    // The ledger category key is still the US 'Labor' (written by the invoice
    // posting functions); read 'Labour' too so a later rename needs no UI change.
    const labourIncome = (data.revenue['Labour'] || 0) + (data.revenue['Labor'] || 0);
    const partsIncome = data.revenue['Parts'] || data.revenue['Margin'] || 0;
    const totalRevenue = data.revenue_total;

    // COGS from new structure
    const cogs = data.cogs || {};
    const cogsTotal = data.cogs_total || 0;
    const grossProfit = data.gross_profit || (totalRevenue - cogsTotal);

    // OPEX from new structure
    const opex = data.opex || {};
    const opexTotal = data.opex_total || 0;

    // Profit metrics
    const taxableProfit = data.taxable_profit || data.net_profit;
    const accountingProfit = data.accounting_profit || taxableProfit;

    return (
        <div className="w-full p-8 flex flex-col bg-card text-fg rounded-xl border border-line shadow-xl">
            {/* Header */}
            <h3 className="text-3xl font-display font-bold tracking-tight mb-8">Profit & Loss Statement</h3>

            {/* REVENUE */}
            <section className="mb-6">
                <p className="text-xs font-display font-semibold uppercase text-fg-4 tracking-wider mb-3">Revenue</p>
                <div className="space-y-1">
                    <LineItem label="Labour Income" value={labourIncome} indent />
                    <LineItem label="Parts Income" value={partsIncome} indent />
                </div>
                <div className="mt-3">
                    <TotalRow label="Total Revenue" value={totalRevenue} />
                </div>
            </section>

            {/* COGS */}
            <section className="mb-6">
                <p className="text-xs font-display font-semibold uppercase text-fg-4 tracking-wider mb-3">Cost of Goods Sold (COGS)</p>
                <div className="space-y-1">
                    {Object.entries(cogs).map(([name, amount]) => (
                        <LineItem key={name} label={name} value={amount as number} indent />
                    ))}
                    {Object.keys(cogs).length === 0 && (
                        <div className="text-sm text-fg-3 italic pl-8">No COGS recorded</div>
                    )}
                </div>
                <div className="mt-3">
                    <TotalRow label="Total COGS" value={cogsTotal} />
                </div>
            </section>

            {/* GROSS PROFIT */}
            <section className="mb-6">
                <TotalRow label="Gross Profit" value={grossProfit} accent />
            </section>

            {/* OPEX */}
            <section className="mb-6">
                <p className="text-xs font-display font-semibold uppercase text-fg-4 tracking-wider mb-3">Operating Expenses (OPEX)</p>
                <div className="space-y-1">
                    {Object.entries(opex).map(([name, amount]) => (
                        <LineItem key={name} label={name} value={amount as number} indent />
                    ))}
                    {Object.keys(opex).length === 0 && (
                        <div className="text-sm text-fg-3 italic pl-8">No OPEX recorded</div>
                    )}
                </div>
                <div className="mt-3">
                    <TotalRow label="Total Opex" value={opexTotal} />
                </div>
            </section>

            {/* TAX RECONCILIATION */}
            <section className="pt-6 border-t border-line">
                <div className="flex justify-between items-center mb-2">
                    <span className="text-lg text-fg-3">Taxable Profit</span>
                    <span className={`text-2xl font-bold font-mono tabular-nums ${taxableProfit < 0 ? 'text-danger' : 'text-success'}`}>
                        {formatMoney(taxableProfit)}
                    </span>
                </div>
                <div className="flex justify-between items-center text-sm text-fg-4">
                    <span>Accounting Profit (after drawings)</span>
                    <span className="font-mono tabular-nums">{formatMoney(accountingProfit)}</span>
                </div>
            </section>
        </div>
    );
};
